/**
 * Modal for adding companies to watchlist with intelligent search
 */

import { useState, useEffect, useRef } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import { useCompanySearch } from '../../hooks/useCompanySearch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CompanySearchCandidate, CompanyType } from '../../types';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function AddCompanyModal({ isOpen, onClose }: AddCompanyModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    query,
    setQuery,
    results,
    isSearching,
    searchError,
    search,
    clearResults,
    addCompany,
    isAdding,
    addError,
  } = useCompanySearch();

  const [selectedCandidate, setSelectedCandidate] = useState<CompanySearchCandidate | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Manual entry state
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDomain, setManualDomain] = useState('');
  const [manualTicker, setManualTicker] = useState('');
  const [manualType, setManualType] = useState<CompanyType>('private');

  const debouncedQuery = useDebounce(query, 400);

  // Auto-search on debounced query change
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      search(debouncedQuery);
    }
  }, [debouncedQuery, search]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      clearResults();
      setSelectedCandidate(null);
      setSuccessMessage(null);
      setIsManualMode(false);
      setManualName('');
      setManualDomain('');
      setManualTicker('');
      setManualType('private');
    }
  }, [isOpen, setQuery, clearResults]);

  // Extract domain from URL
  const extractDomain = (input: string): string => {
    try {
      // If it looks like a URL, extract domain
      if (input.includes('://')) {
        const url = new URL(input);
        return url.hostname.replace(/^www\./, '');
      }
      // If it has a dot but no protocol, assume it's already a domain
      if (input.includes('.')) {
        return input.replace(/^www\./, '').toLowerCase();
      }
      return input.toLowerCase();
    } catch {
      return input.toLowerCase();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleAddCompany = async () => {
    // Handle manual entry
    if (isManualMode) {
      if (!manualName.trim() || !manualDomain.trim()) return;

      const domain = extractDomain(manualDomain.trim());
      const manualCandidate: CompanySearchCandidate = {
        name: manualName.trim(),
        domain,
        ticker: manualTicker.trim() || undefined,
        company_type: manualType,
        description: undefined,
        confidence: 1.0,
        already_tracked: false,
      };

      try {
        await addCompany(manualCandidate);
        setSuccessMessage(`${manualCandidate.name} added to watchlist. Knowledge base build started.`);
        setTimeout(() => {
          onClose();
        }, 2000);
      } catch {
        // Error handled by hook
      }
      return;
    }

    // Handle search selection
    if (!selectedCandidate) return;

    try {
      await addCompany(selectedCandidate);
      setSuccessMessage(`${selectedCandidate.name} added to watchlist. Knowledge base build started.`);
      setSelectedCandidate(null);
      // Close after brief delay to show success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch {
      // Error handled by hook
    }
  };

  const getCompanyTypeLabel = (type: CompanyType) => {
    switch (type) {
      case 'public': return 'Public';
      case 'pe_backed': return 'PE-Backed';
      case 'private': return 'Private';
      case 'subsidiary': return 'Subsidiary';
      default: return type;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-amber-600';
    return 'text-gray-500';
  };

  const isAddDisabled = isManualMode
    ? (!manualName.trim() || !manualDomain.trim() || isAdding)
    : (!selectedCandidate || isAdding);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Company to Watchlist</DialogTitle>
          <DialogDescription>
            Search for a company or add one manually to start building its knowledge base.
          </DialogDescription>
        </DialogHeader>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">{successMessage}</span>
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="mb-4">
          <Label htmlFor="company-search" className="mb-1.5">
            Search for a company
          </Label>
          <div className="relative">
            <Input
              ref={inputRef}
              id="company-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter company name (e.g., Veeva Systems)"
              data-testid="company-search"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            We'll search web sources and validate the company with AI
          </p>
        </div>

        {/* Error State */}
        {(searchError || addError) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <span className="text-sm text-red-700">
              {searchError?.message || addError?.message || 'An error occurred'}
            </span>
          </div>
        )}

        {/* Already Tracked */}
        {results?.internal_matches && results.internal_matches.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Already Tracking</h3>
            <div className="space-y-2">
              {results.internal_matches.map((company) => (
                <div
                  key={company.id}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-md opacity-60"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-700">{company.name}</span>
                      {company.ticker && (
                        <span className="ml-2 text-sm text-gray-500">({company.ticker})</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                      Already tracked
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{company.domain}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {results?.external_candidates && results.external_candidates.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Search Results</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {results.external_candidates.map((candidate, idx) => (
                <button
                  key={`${candidate.domain}-${idx}`}
                  onClick={() => setSelectedCandidate(
                    selectedCandidate?.domain === candidate.domain ? null : candidate
                  )}
                  disabled={candidate.already_tracked}
                  className={`w-full text-left p-3 border rounded-md transition-colors ${
                    selectedCandidate?.domain === candidate.domain
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : candidate.already_tracked
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {selectedCandidate?.domain === candidate.domain && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                        <span className="font-medium text-gray-900">{candidate.name}</span>
                        {candidate.ticker && (
                          <span className="text-sm text-gray-500">({candidate.ticker})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {getCompanyTypeLabel(candidate.company_type)}
                        </span>
                        <span className="text-xs text-gray-500">{candidate.domain}</span>
                      </div>
                      {candidate.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {candidate.description}
                        </p>
                      )}
                    </div>
                    <div className="ml-2 text-right">
                      <span className={`text-xs font-medium ${getConfidenceColor(candidate.confidence)}`}>
                        {Math.round(candidate.confidence * 100)}% match
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {!isManualMode && query.length >= 2 && !isSearching && results &&
         results.internal_matches.length === 0 &&
         results.external_candidates.length === 0 && (
          <div className="mb-4 p-4 text-center text-gray-500 bg-gray-50 rounded-md">
            <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No companies found for "{query}"</p>
            <p className="text-xs mt-1">Try a different search term or add manually</p>
            <button
              onClick={() => {
                setIsManualMode(true);
                setManualName(query);
              }}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Add company manually →
            </button>
          </div>
        )}

        {/* Mode Toggle */}
        {!isManualMode && !successMessage && (
          <div className="mb-4 text-center">
            <button
              onClick={() => setIsManualMode(true)}
              className="text-sm text-gray-500 hover:text-primary-600"
            >
              Can't find your company? <span className="font-medium text-primary-600">Add manually</span>
            </button>
          </div>
        )}

        {/* Manual Entry Form */}
        {isManualMode && !successMessage && (
          <div className="mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">Manual Entry</h3>
              <button
                onClick={() => setIsManualMode(false)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                ← Back to search
              </button>
            </div>

            <div>
              <Label htmlFor="manual-name" className="mb-1.5">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="manual-name"
                type="text"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="DrFirst, Inc."
              />
            </div>

            <div>
              <Label htmlFor="manual-domain" className="mb-1.5">
                Website URL or Domain <span className="text-red-500">*</span>
              </Label>
              <Input
                id="manual-domain"
                type="text"
                value={manualDomain}
                onChange={(e) => setManualDomain(e.target.value)}
                placeholder="drfirst.com or https://www.drfirst.com"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the company's website URL or domain
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="manual-ticker" className="mb-1.5">
                  Ticker Symbol
                </Label>
                <Input
                  id="manual-ticker"
                  type="text"
                  value={manualTicker}
                  onChange={(e) => setManualTicker(e.target.value.toUpperCase())}
                  placeholder="e.g., AAPL"
                  className="uppercase"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave blank if private
                </p>
              </div>

              <div>
                <Label htmlFor="manual-type" className="mb-1.5">
                  Company Type
                </Label>
                <Select value={manualType} onValueChange={(value) => setManualType(value as CompanyType)}>
                  <SelectTrigger id="manual-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="pe_backed">PE-Backed</SelectItem>
                    <SelectItem value="subsidiary">Subsidiary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCompany}
            disabled={isAddDisabled}
          >
            {isAdding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : isManualMode ? (
              'Add Company'
            ) : (
              'Add Selected Company'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddCompanyModal;
